import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "reactstrap";
import { toast } from "react-toastify";

import ClientModal from "../../Client/ClientModal/ClientModal";
import { useVatRates } from "../../../Components/Hooks/useVatRates";
import { wizardAPI } from "./api";
import {
  CONTRACT_TYPES,
  EDITABLE_PAYMENT_TYPES,
  EXTRACTED_TYPE_MAP,
  PAYMENT_TYPE_LABELS,
  SECTION_LABELS,
  SEVERITY_ORDER,
  STEP_TITLES,
  UNIT_TYPE_LABEL,
} from "./constants";
import { btn, primaryBtn, scopedCss, t } from "./styles";
import { buildContractPayload, computeTotals, ddmmyyyyToIso, fmtDate, money, n, sortByDate, unitFromDetail, unitFromList } from "./utils";
import Stepper from "./components/Stepper";
import ImportStep from "./steps/ImportStep";
import DetailsStep from "./steps/DetailsStep";
import UnitsStep from "./steps/UnitsStep";
import PaymentsStep from "./steps/PaymentsStep";

const PVC_CHECK_DEBOUNCE_MS = 800;
let uid = 0;
const nextId = () => ++uid;

const emptyState = (clientId = "") => ({
  step: 0,
  reached: 0,
  importState: "idle", // idle | reading | done | error
  importError: "", // the backend's reason when importState is "error"
  file: null,
  extracted: null, // what ImportStep shows after a successful read
  docValues: {}, // values read from the document, for "From document" tags
  rawClient: null, // client found in the document but not in the system
  rawSecondaryClient: null, // a second client named in the document, also not in the system
  d: { pvc: "", date: "", client: clientId, type: "", currency: "RON", notary: "", description: "" },
  f: { project: "", phase: "", block: "", type: "APT", q: "" },
  sel: [], // [{ id, unit, settle, extra }]
  pays: [{ id: "extras" }], // [{ id, type, amount, vat, date }] + the extras placeholder
  extrasDate: "",
  extrasVat: "",
  balanceVat: "",
  pvcStatus: "idle", // idle | checking | available | unavailable
});

const importSummary = ({ found, total, units, payments, note }, issues) =>
  `${found} of ${total} fields · ${units} ${units === 1 ? "unit" : "units"} · ` +
  `${payments} payments extracted · ${issues} ${issues === 1 ? "issue" : "issues"} flagged${note}`;

const withClientCreated = (extracted) => {
  if (!extracted) return extracted;
  const findings = extracted.findings.filter((f) => f.kind !== "client");
  return {
    ...extracted,
    findings,
    fields: extracted.fields.map((f) => (f.key === "client" ? { ...f, value: "Created from the document" } : f)),
    summary: importSummary(extracted.stats, findings.length),
  };
};

// ClientModal's pre-fill shape (same normalisation as the V1 wizard).
const toClientModalData = (p) =>
  p?.type === "LEGAL"
    ? {
        legal_company_name: p.legal_company_name || "",
        legal_fiscal_code: p.legal_fiscal_code || "",
        legal_trade_register_number: p.legal_trade_register_number || "",
        country: p.country || "",
        region: p.region || "",
        city: p.city || "",
        address: p.address || "",
        business_email: p.business_email || "",
        mobile_phone_number: p.mobile_phone_number || "",
      }
    : {
        name: p?.name || "",
        surname: p?.surname || "",
        social_security_number: p?.ssn || p?.social_security_number || "",
        social_security_series: p?.ssn_series || p?.social_security_series || "",
        mobile_phone_number: p?.phone || p?.mobile_phone_number || "",
        personal_email: p?.email || p?.personal_email || "",
        address: p?.address || "",
        country: p?.country || "",
        city: p?.city || "",
        region: p?.region || "",
      };

const rawClientName = (p) =>
  p?.type === "LEGAL" ? p.legal_company_name : [p?.name, p?.surname].filter(Boolean).join(" ");

const formatError = (err) => {
  const details = err?.details;
  if (details && typeof details === "object") {
    const flat = [];
    const walk = (v, prefix) => {
      if (Array.isArray(v)) v.forEach((x) => (typeof x === "string" ? flat.push(prefix ? `${prefix}: ${x}` : x) : walk(x, prefix)));
      else if (v && typeof v === "object") Object.entries(v).forEach(([k, x]) => walk(x, k === "non_field_errors" ? prefix : k));
      else if (v) flat.push(prefix ? `${prefix}: ${v}` : String(v));
    };
    walk(details, "");
    if (flat.length) return flat.join(" · ");
  }
  return err?.message || "Failed to create the contract";
};

/**
 * New contract wizard (V2): import a document (optional) -> details ->
 * units -> payments -> POST /api/contracts/.
 *
 * Props:
 *   isOpen, onClose
 *   clientDetails  optional client to pre-select (e.g. opened from a client page)
 *   onCreated      called with the created contract
 */
const ContractWizardV2 = ({ isOpen, onClose, clientDetails, onCreated }) => {
  const [st, setSt] = useState(() => emptyState(clientDetails?.id));
  const [projects, setProjects] = useState([]);
  const [blockUnits, setBlockUnits] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [rate, setRate] = useState(null);
  const [clientModal, setClientModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { vatRates } = useVatRates();
  const pvcRequest = useRef(0);

  const patch = useCallback((p) => setSt((s) => ({ ...s, ...(typeof p === "function" ? p(s) : p) })), []);
  const setD = useCallback((k, v) => setSt((s) => ({ ...s, d: { ...s.d, [k]: v } })), []);
  const setF = useCallback((p) => setSt((s) => ({ ...s, f: { ...s.f, ...p } })), []);

  // Fresh wizard every time it opens.
  useEffect(() => {
    if (isOpen) {
      setSt(emptyState(clientDetails?.id));
      setBlockUnits([]);
    }
  }, [isOpen, clientDetails?.id]);

  useEffect(() => {
    if (!isOpen) return;
    wizardAPI
      .projects()
      .then((res) => setProjects(Array.isArray(res) ? res : res?.results || []))
      .catch(() => setProjects([]));
    wizardAPI
      .exchangeRate()
      .then((res) => res?.rate && setRate({ rate: Number(res.rate), date: res.date }))
      .catch(() => setRate(null));
  }, [isOpen]);

  // Default every VAT picker to the first (highest) rate once rates arrive.
  useEffect(() => {
    if (!vatRates.length) return;
    const first = String(vatRates[0].id);
    setSt((s) => ({
      ...s,
      extrasVat: s.extrasVat || first,
      balanceVat: s.balanceVat || first,
      pays: s.pays.map((p) => (p.id !== "extras" && !p.vat ? { ...p, vat: first } : p)),
    }));
  }, [vatRates]);

  useEffect(() => {
    if (!st.f.block) {
      setBlockUnits([]);
      return;
    }
    let cancelled = false;
    setBlockLoading(true);
    wizardAPI
      .unitsInBlock(st.f.block)
      .then((res) => {
        if (cancelled) return;
        const list = (Array.isArray(res) ? res : res?.results || []).map(unitFromList);
        setBlockUnits(list);
        // Land on a unit type the block actually has.
        if (list.length && !list.some((u) => u.type === st.f.type)) setF({ type: list[0].type });
      })
      .catch(() => !cancelled && setBlockUnits([]))
      .finally(() => !cancelled && setBlockLoading(false));
    return () => {
      cancelled = true;
    };
  }, [st.f.block, setF]);

  useEffect(() => {
    if (!st.d.client) {
      setClient(null);
      return;
    }
    let cancelled = false;
    wizardAPI
      .client(st.d.client)
      .then((res) => !cancelled && setClient(res?.data || res))
      .catch(() => !cancelled && setClient(null));
    return () => {
      cancelled = true;
    };
  }, [st.d.client]);

  // PVC numbers are unique per year of the PVC date.
  useEffect(() => {
    const { pvc, date } = st.d;
    if (!pvc || !date) {
      patch({ pvcStatus: "idle" });
      return;
    }
    patch({ pvcStatus: "checking" });
    const id = ++pvcRequest.current;
    const handle = setTimeout(async () => {
      try {
        const res = await wizardAPI.checkPvc(pvc, new Date(date).getFullYear());
        if (id === pvcRequest.current) patch({ pvcStatus: res?.available === false ? "unavailable" : "available" });
      } catch (e) {
        if (id === pvcRequest.current) patch({ pvcStatus: "idle" });
      }
    }, PVC_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [st.d.pvc, st.d.date, patch]);

  // ---- document import -------------------------------------------------------

  const vatIdFor = (pct) => {
    const match = vatRates.find((v) => Number(v.percentage) === Number(pct));
    return match ? String(match.id) : vatRates[0] ? String(vatRates[0].id) : "";
  };

  // `res` is the v2 extraction response:
  // { data, contract_inconsistency, sections_missing, meta }.
  const applyExtraction = async (res) => {
    const data = res?.data || {};
    const inconsistencies = res?.contract_inconsistency || [];
    const sectionsMissing = res?.sections_missing || [];
    const meta = res?.meta || {};
    const cd = data.contract_data || {};
    const personal = data.personal_data || null;
    const clientId = personal?.id ? personal.id : "";
    const rawClient = personal && !personal.id ? personal : null;
    const secondary = data.secondary_personal_data || null;
    const rawSecondaryClient = rawClient && secondary && !secondary.id ? secondary : null;

    const settlements = data.settlement_data?.settlements || [];
    const matched = settlements.filter((s) => s.unit_id);
    const unmatched = settlements.filter((s) => !s.unit_id);
    const details = await Promise.all(matched.map((s) => wizardAPI.unit(s.unit_id).catch(() => null)));
    const sel = matched
      .map((s, i) => (details[i]?.unit ? { id: s.unit_id, unit: unitFromDetail(details[i]), settle: String(s.amount ?? ""), extra: "" } : null))
      .filter(Boolean);

    const extractedPays = data.payments_data || [];
    const pays = extractedPays
      .filter((p) => p.type !== "BALANCE")
      .map((p) => ({
        id: nextId(),
        // The extractor already reports Payment.type values.
        type: EDITABLE_PAYMENT_TYPES.some((o) => o.value === p.type) ? p.type : "OTHER",
        amount: String(p.amount ?? ""),
        vat: vatIdFor(p.vat_percentage),
        date: ddmmyyyyToIso(p.booking_date),
      }));

    const firstUnit = data.property_data?.units?.find((u) => u.block) || {};
    const type = EXTRACTED_TYPE_MAP[cd.contract_type] ?? "";

    // ---- contract review.
    // 1. What Claude found by checking the contract against itself (amounts
    //    in digits vs words, payments vs price, dates, CNP, ...), most severe first.
    const findings = [...inconsistencies]
      .sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3))
      .map((k) => ({ severity: k.severity, title: k.details, detail: "" }));
    // 2. Contract sections that weren't in the document at all.
    sectionsMissing.forEach((name) =>
      findings.push({
        severity: "Medium",
        title: `Section not found: ${SECTION_LABELS[name] || name}`,
        detail: "Nothing from it could be read — check the document or fill these values in by hand.",
      })
    );
    // 3. What only the database can tell: units / client not matching.
    unmatched.forEach((s) =>
      findings.push({
        severity: "High",
        title: "Unit not found in the inventory",
        detail: `${UNIT_TYPE_LABEL[s.unit_type] || s.unit_type || "Unit"}${s.unit_nr ? ` nr. ${s.unit_nr}` : ""}${s.unit_letter ? ` type ${s.unit_letter}` : ""} (${money(n(s.amount))}) couldn't be matched to a unit — add it by hand in the Units step.`,
        where: "Section 3.1",
      })
    );
    sel.filter((s) => s.unit.sold).forEach((s) =>
      findings.push({ severity: "High", title: "Unit is already sold", detail: `${s.unit.label} already belongs to another contract.`, where: s.unit.path })
    );
    if (rawClient) {
      findings.push({
        severity: "Medium",
        kind: "client",
        title: "Client isn’t in the system yet",
        detail: `${rawClientName(rawClient) || "The client"} from the document has no matching client — create them from the pre-filled form (also under “+ Create from document” in Contract details).`,
        where: "Section 1",
      });
    }
    if (cd.contract_type === "SALE") {
      findings.push({ severity: "Low", title: "Contract type to choose", detail: "The document is a sale contract — pick Cash or Credit in the next step." });
    }

    const clientLabel = clientId
      ? "Matched to an existing client"
      : rawClient
      ? `${rawClientName(rawClient)} · not in the system`
      : "";
    const fields = [
      { label: "PVC number", value: cd.pvc_number || "" },
      { label: "PVC date", value: fmtDate(cd.pvc_date) },
      { key: "client", label: "Client", value: clientLabel },
      { label: "Contract type", value: CONTRACT_TYPES.find((c) => c.value === type)?.label || "" },
      { label: "Notary", value: cd.notary || "" },
    ];
    const found = fields.filter((f) => f.value).length;
    const balance = extractedPays.find((p) => p.type === "BALANCE");
    const shownPays = pays
      .map((p) => ({ label: PAYMENT_TYPE_LABELS[p.type] || p.type, date: p.date, amount: n(p.amount) }))
      .concat(balance ? [{ label: PAYMENT_TYPE_LABELS.BALANCE, date: "", amount: n(balance.amount) }] : []);

    const stats = {
      found,
      total: fields.length,
      units: sel.length,
      payments: shownPays.length,
      note:
        (meta.cached ? " · from cache, no cost" : meta.estimated_cost_usd ? ` · ~$${Number(meta.estimated_cost_usd).toFixed(3)}` : "") +
        (meta.extractions_left_today != null ? ` · ${meta.extractions_left_today} extractions left today` : ""),
    };

    setSt((s) => ({
      ...s,
      importState: "done",
      extracted: {
        fields,
        units: sel.map((x) => ({ id: x.id, label: x.unit.label, path: x.unit.path, settle: n(x.settle) })),
        payments: shownPays,
        findings,
        pvc: cd.pvc_number,
        stats,
        summary: importSummary(stats, findings.length),
      },
      docValues: { pvc: cd.pvc_number || "", date: cd.pvc_date || "", client: clientId, type, notary: cd.notary || "" },
      rawClient,
      rawSecondaryClient,
      d: {
        ...s.d,
        pvc: cd.pvc_number || s.d.pvc,
        date: cd.pvc_date || s.d.date,
        client: clientId || s.d.client,
        type: type || s.d.type,
        notary: cd.notary || s.d.notary,
      },
      f: firstUnit.block ? { project: String(firstUnit.project || ""), phase: String(firstUnit.phase || ""), block: String(firstUnit.block), type: sel[0]?.unit.type || "APT", q: "" } : s.f,
      sel: sel.length ? sel : s.sel,
      pays: pays.length ? sortByDate(pays.concat({ id: "extras" }), s.extrasDate) : s.pays,
    }));

    if (rawClient) setClientModal(true);
  };

  const pickFile = async (file) => {
    patch({ importState: "reading", file, importError: "" });
    try {
      const res = await wizardAPI.extract(file);
      await applyExtraction(res);
    } catch (e) {
      patch({ importState: "error", importError: e?.message || "" });
    }
  };

  // ---- steps ---------------------------------------------------------------

  const totals = useMemo(() => computeTotals(st), [st]);
  const go = (i) => patch((s) => ({ step: i, reached: Math.max(s.reached, i) }));

  const toggleUnit = (u) =>
    patch((s) => ({
      sel: s.sel.some((x) => x.id === u.id) ? s.sel.filter((x) => x.id !== u.id) : s.sel.concat({ id: u.id, unit: u, settle: String(u.price || ""), extra: "" }),
    }));
  const updateSel = (id, p) => patch((s) => ({ sel: s.sel.map((x) => (x.id === id ? { ...x, ...p } : x)) }));
  const removeSel = (id) => patch((s) => ({ sel: s.sel.filter((x) => x.id !== id) }));

  const updatePay = (id, p) =>
    patch((s) => {
      const pays = s.pays.map((x) => (x.id === id ? { ...x, ...p } : x));
      return { pays: "date" in p ? sortByDate(pays, s.extrasDate) : pays };
    });
  const movePay = (fromId, toId) =>
    patch((s) => {
      const from = s.pays.findIndex((p) => p.id === fromId);
      if (from < 0) return {};
      const next = s.pays.slice();
      const [item] = next.splice(from, 1);
      if (toId === "balance") next.push(item);
      else {
        const to = next.findIndex((p) => p.id === toId);
        next.splice(to < 0 ? next.length : to, 0, item);
      }
      return { pays: next };
    });
  const addPay = () =>
    patch((s) => ({
      pays: s.pays.concat({
        id: nextId(),
        type: s.pays.some((p) => p.id !== "extras") ? "PAYMENT" : "DOWN_PAYMENT",
        amount: "",
        vat: s.balanceVat,
        date: "",
      }),
    }));

  // ---- submit --------------------------------------------------------------

  const submit = async () => {
    setSubmitting(true);
    try {
      const created = await wizardAPI.createContract(buildContractPayload(st, totals));
      if (st.file && created?.id) {
        try {
          await wizardAPI.attachDocument(created.id, st.file);
        } catch (e) {
          toast.warning("Contract created, but the document couldn't be attached — upload it from the contract page.", { autoClose: 5000 });
        }
      }
      toast.success(`Contract PVC–${st.d.pvc} created · ${money(totals.total)}`, { autoClose: 3500 });
      onCreated?.(created);
      onClose();
    } catch (err) {
      toast.error(formatError(err), { autoClose: 6000 });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- render --------------------------------------------------------------

  const { step } = st;
  const err = totals.valid[step];
  const last = step === 3;
  const reading = st.importState === "reading";

  const clientName = client
    ? client.buyer_type === "LEGAL"
      ? client.legal_company_name || client.name
      : `${client.name || ""} ${client.surname || ""}`.trim()
    : "";

  const steps = [
    { label: STEP_TITLES[0], sub: st.importState === "done" ? "Data extracted" : "Optional" },
    { label: STEP_TITLES[1], sub: st.d.pvc ? `PVC–${st.d.pvc}` : "PVC, client, type" },
    { label: STEP_TITLES[2], sub: st.sel.length ? `${st.sel.length} ${st.sel.length === 1 ? "unit" : "units"} · ${money(totals.total)}` : "Select units & prices" },
    { label: STEP_TITLES[3], sub: "Schedule & balance" },
  ];

  let footText = err;
  if (!err) {
    if (step === 0) footText = st.importState === "done" ? "Extracted data will pre-fill the next steps" : reading ? "Reading document…" : "Import a document or enter the contract manually";
    else if (step === 1) footText = "Details complete";
    else if (step === 2) footText = `${st.sel.length} ${st.sel.length === 1 ? "unit" : "units"} · contract total ${money(totals.total)}`;
    else footText = `Payments add up to ${money(totals.total)}`;
  }

  const nextDisabled = !!err || reading || submitting;
  const nextLabel = last ? (submitting ? "Creating…" : "Create contract") : step === 0 && st.importState !== "done" ? "Skip import" : "Continue";
  const showRon = st.d.currency === "RON";
  const fromDoc = Object.fromEntries(
    ["pvc", "date", "client", "type", "notary"].map((k) => [k, st.importState === "done" && !!st.docValues[k] && String(st.d[k]) === String(st.docValues[k])])
  );

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} centered contentClassName="border-0 bg-transparent" style={{ maxWidth: 1100 }}>
        <style>{scopedCss}</style>
        <div
          className="cw2"
          style={{
            width: "100%",
            height: "min(880px, calc(100vh - 56px))",
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 30px 80px rgba(20,20,32,0.3)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "18px 24px 0", borderBottom: "1px solid #eeeef2" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 650, letterSpacing: -0.3 }}>New contract</div>
                <div style={{ fontSize: 12.5, color: "#6f6f7c", marginTop: 3 }}>
                  {st.d.pvc ? `PVC–${st.d.pvc}${clientName ? ` · ${clientName}` : ""}` : `Step ${step + 1} of 4`}
                </div>
              </div>
              <button
                type="button"
                className="cw2-btn"
                onClick={onClose}
                style={{ flex: "none", font: "inherit", fontSize: 15, lineHeight: 1, width: 32, height: 32, borderRadius: 8, border: `1px solid ${t.border}`, background: "#fff", color: t.textSecondary, cursor: "pointer" }}
              >
                ✕
              </button>
            </div>
            <Stepper steps={steps} current={step} reached={st.reached} valid={totals.valid} onGo={(i) => patch({ step: i })} />
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "22px 24px" }}>
            {step === 0 && (
              <ImportStep
                importState={st.importState}
                importError={st.importError}
                file={st.file}
                extracted={st.extracted}
                onPick={pickFile}
                onReplace={() => patch({ importState: "idle" })}
                onSkip={() => go(1)}
              />
            )}
            {step === 1 && (
              <DetailsStep
                d={st.d}
                setD={setD}
                tried={st.reached > 1}
                fromDoc={fromDoc}
                notaryMissingInDoc={st.importState === "done" && !st.docValues.notary}
                pvcStatus={st.pvcStatus}
                client={client}
                rate={rate}
                clientNotInSystem={!!st.rawClient}
                onNewClient={() => setClientModal(true)}
              />
            )}
            {step === 2 && (
              <UnitsStep
                projects={projects}
                f={st.f}
                setF={setF}
                blockUnits={blockUnits}
                blockLoading={blockLoading}
                sel={st.sel}
                onToggle={toggleUnit}
                onUpdateSel={updateSel}
                onRemove={removeSel}
                totals={totals}
                showRon={showRon}
                rate={rate?.rate}
              />
            )}
            {step === 3 && (
              <PaymentsStep
                pays={st.pays}
                extrasDate={st.extrasDate}
                extrasVat={st.extrasVat}
                balanceVat={st.balanceVat}
                vatRates={vatRates}
                totals={totals}
                showRon={showRon}
                rate={rate?.rate}
                onUpdatePay={updatePay}
                onRemovePay={(id) => patch((s) => ({ pays: s.pays.filter((p) => p.id !== id) }))}
                onAddPay={addPay}
                onExtrasDate={(v) => patch((s) => ({ extrasDate: v, pays: sortByDate(s.pays, v) }))}
                onExtrasVat={(v) => patch({ extrasVat: v })}
                onBalanceVat={(v) => patch({ balanceVat: v })}
                onAllVat={(v) => patch((s) => ({ pays: s.pays.map((p) => (p.id === "extras" ? p : { ...p, vat: v })), extrasVat: v, balanceVat: v }))}
                onMove={movePay}
              />
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 24px", borderTop: "1px solid #eeeef2", background: t.surfaceSubtle, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: err ? "#d24545" : t.successBar, flex: "none" }} />
              <span style={{ fontSize: 12.5, color: err ? t.dangerText : "#3c3c46" }}>{footText}</span>
            </div>
            {step > 0 && (
              <button type="button" className="cw2-btn" onClick={() => patch({ step: step - 1 })} style={btn}>
                Back
              </button>
            )}
            <button
              type="button"
              className="cw2-primary"
              disabled={nextDisabled}
              onClick={() => (last ? submit() : go(step + 1))}
              style={{ ...primaryBtn, padding: "9px 18px", background: nextDisabled ? "#c3c0e6" : t.accent, cursor: nextDisabled ? "not-allowed" : "pointer" }}
            >
              {nextLabel}
            </button>
          </div>
        </div>
      </Modal>

      <ClientModal
        show={clientModal}
        clientOption={st.rawClient?.type === "LEGAL" ? "LEGAL" : "NATURAL"}
        onCloseClick={() => setClientModal(false)}
        extractedPersonalData={st.rawClient ? toClientModalData(st.rawClient) : null}
        extractedSecondaryPersonalData={st.rawSecondaryClient ? toClientModalData(st.rawSecondaryClient) : null}
        onClientCreated={(id) => {
          setClientModal(false);
          patch((s) => ({ rawClient: null, rawSecondaryClient: null, extracted: withClientCreated(s.extracted), d: { ...s.d, client: id } }));
        }}
      />
    </>
  );
};

export default ContractWizardV2;
